import { api, queryKeys, type Rank } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { formatLds, formatNumber, formatUsd } from '../../lib/format';
import { Card, Chip, ProgressBar, Spinner, StatCard, StatCards } from '../../ui';

const FAMILY_LABELS: Record<Rank['family'], string> = {
  alpha_a: 'Alpha Club A',
  alpha_b: 'Alpha Club B',
  elite: 'Elite Ranks',
};

function RankGroup({ title, ranks }: { title: string; ranks: Rank[] }) {
  if (ranks.length === 0) return null;
  return (
    <Card>
      <h3 className="card-title">{title}</h3>
      <ul className="tier-list">
        {ranks.map((rank) => (
          <li key={rank.id} className="tier-row">
            <div className="tier-head">
              <strong>{rank.name}</strong>
              {rank.achieved ? <Chip tone="success">Achieved</Chip> : <Chip tone="gold">Locked</Chip>}
              <span className="text-gold">
                {formatUsd(rank.reward_usd)} <span className="text-muted small">({formatLds(rank.reward_lds)})</span>
              </span>
            </div>
            <p className="text-muted small">{rank.requirement_label}</p>
            <ProgressBar
              value={rank.progress_current}
              max={rank.progress_required}
              label={`${formatNumber(rank.progress_current)} / ${formatNumber(rank.progress_required)}`}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function RankAchievement() {
  const { data: ranks, isLoading } = useQuery({
    queryKey: queryKeys.ranks(),
    queryFn: () => api.ranks.list().then((r) => r.data),
  });

  const current = useQuery({
    queryKey: ['ranks', 'current'],
    queryFn: () => api.ranks.current().then((r) => r.data),
  });

  if (isLoading) return <Spinner />;

  const byFamily = (family: Rank['family']) => (ranks ?? []).filter((r) => r.family === family);

  return (
    <>
      <PageHeader title="Rank Achievement" description="Your progress across the Alpha One rank programmes." />

      <StatCards>
        <StatCard label="Alpha Club A" value={current.data?.alpha_a?.name ?? '—'} />
        <StatCard label="Alpha Club B" value={current.data?.alpha_b?.name ?? '—'} />
        <StatCard label="Elite Rank" value={current.data?.elite?.name ?? '—'} />
        <StatCard label="Next Rank" value={current.data?.next?.name ?? '—'} />
      </StatCards>

      <RankGroup title={FAMILY_LABELS.alpha_a} ranks={byFamily('alpha_a')} />
      <RankGroup title={FAMILY_LABELS.alpha_b} ranks={byFamily('alpha_b')} />
      <RankGroup title={FAMILY_LABELS.elite} ranks={byFamily('elite')} />
    </>
  );
}
